import { yupResolver } from '@hookform/resolvers/yup'
import { t } from '@if/core/i18n'
import {
  CreditDecision,
  EBeneficiaryType,
  ECreditApplicationStatus,
  EFinancingProgram,
  EHomeStatus,
  ELoanPurpose,
  EOriginSystemId,
  EOtherIncomeType,
  EPaymentPlan,
  ERelationToApplicant,
  getEBeneficiaryTypeList,
  getECreditApplicationStatusList,
  getEFinancingProgramList,
  getEHomeStatusList,
  getELoanPurposeList,
  getEOriginSystemIdList,
  getERelationToApplicantList,
} from '@if/core/types'
import * as yup from '@if/core/yup'
import { resolveMaxLoanAmount } from '@src/services/loan-amount-limits'
import { get } from 'lodash-es'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'
import { AddressSchema, FullAddressSchema } from './AddressSchema'
import { BannedBankingInformation, Constants, defaultProgramConfig } from './constants'
import { ApplicantCreditDecision, CreditDecisionOverrides, NormCreditDecision } from './CreditDecisionSchema'
import { ExistingCustomerReference } from './CustomerReferenceSchema'
import { FinancingConfigDto } from './FinancingConfigDto'
import { HardHitReport } from './HardHitReport'
import { PrequalificationDecision } from './PrequalificationDecision'
import { Refinancing } from './RefinancingSchema'
import { SoftHitReport } from './SoftHitReport'

export const expensesSchema = yup.object({
  financingProgramId: yup.string().nullable().default(''),
  monthlyInstallmentLoanPayment: yup.double().min(0),
  monthlyStudentLoanPayment: yup.double().min(0),
  monthlyLineOfCreditPayment: yup.double().min(0),
  monthlyCreditCardsPayment: yup.double().min(0),
  monthlyPaydayLoanPayment: yup.double().min(0),
  monthlyPersonalLoanPayment: yup.double().min(0),
  monthlyOtherLoanPayment: yup.double().min(0),
  monthlyOtherExpensePayment: yup.double().min(0),
  monthlyActiveIFLoanPayment: yup.double().min(0),
  otherExpensePaymentDescription: yup
    .string()
    .nullable()
    .max(50)
    .when('monthlyOtherExpensePayment', {
      is: (val: number) => val > 0,
      then: (schema) => schema.required(),
    }),
  homeFeeTypeId: yup
    .mixed<EHomeStatus>()
    .default(null)
    .nullable()
    .oneOf([null, ...getEHomeStatusList()])
    .when('financingProgramId', {
      is: EFinancingProgram.Personal,
      then: (schema) => schema.required(),
    }),
  rentMonthly: yup.double(),
  mortgageMonthly: yup.double(),
  housingMonthly: yup.double(),
  houseMarketValue: yup.double(),
  landlord: yup.string().nullable().notRequired().default(''),
  monthlyOwnerInsuranceFee: yup.double().min(0),
  monthlyMortgageInsuranceFee: yup.double().min(0),
  monthlyMunicipalAndSchoolTaxPayment: yup.double().min(0),
  monthlyCondominiumFee: yup.double().min(0),
  monthlyPublicServiceFee: yup.double().min(0),
  monthlyRrspRefund: yup.double().min(0),
  monthlyOtherHomeFee: yup.double().min(0),
  isCompleted: yup.bool().default(false).nullable(),
  isLaw134Confirmed: yup.bool().default(false),
})

export type ApplicantExpenses = yup.InferSubmit<typeof expensesSchema>

export const ExpensesSchema = expensesSchema.when(([value]: ApplicantExpenses[], schema) => {
  const additionalFields: Record<string, yup.AnySchema> = {}
  if (value?.houseMarketValue || value?.housingMonthly) {
    additionalFields.homeFeeTypeId = yup.mixed<EHomeStatus>().oneOf(getEHomeStatusList()).required()
  }

  if (value?.homeFeeTypeId) {
    switch (value.homeFeeTypeId) {
      case EHomeStatus.Owner:
        additionalFields.monthlyHomePayment = yup.double().notRequired().nullable()
        break
      case EHomeStatus.OwnMobileHome:
      case EHomeStatus.OwnWithMortgage:
      case EHomeStatus.Renter:
        additionalFields.housingMonthly = yup.double().required().positive()
        additionalFields.houseMarketValue = yup.double().notRequired().nullable().min(0)
        break
      case EHomeStatus.WithRelative:
      case EHomeStatus.ReserveHousing:
      case EHomeStatus.Other:
      default:
        additionalFields.housingMonthly = yup.double().required().min(0)
        additionalFields.houseMarketValue = yup.double().notRequired().nullable().min(0)
        break
    }
  }

  return schema.shape(additionalFields)
})

export const ApplicantAddressSchema = AddressSchema.shape({
  years: yup.integer().min(0),
  months: yup.integer().when('years', {
    is: (value: number | null) => !!value && value > 0,
    then: (schema) => schema.notRequired().min(0),
    otherwise: (schema) => schema.required().min(1),
  }),
})

const ApplicantAddressSchemaWithoutTimeLived = AddressSchema.shape({
  years: yup.integer().min(0),
  months: yup.integer().min(0),
})

export type ApplicantAddress = yup.InferSubmit<typeof ApplicantAddressSchema>

export const JobSchema = yup.object({
  id: yup.string().default(null),
  employerName: yup.string().default('').required(),
  jobTitle: yup.string().default('').required(),
  jobType: yup.integer().required(),
  annualSalary: yup.integer().required().min(1),
  employerPhone: yup
    .string()
    .validCanadianPhone()
    .nullable()
    .when('$financingProgramId', {
      is: EFinancingProgram.CreditRsla,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  employerPhoneExt: yup.string().validPhoneExtension().nullable(),
  address: AddressSchema.nullable().default(null),
  years: yup.integer().min(0),
  months: yup.integer().when('years', {
    is: (value: number | null) => !!value && value > 0,
    then: (schema) => schema.notRequired().min(0),
    otherwise: (schema) => schema.required().min(1),
  }),
  verificationContactName: yup.string().nullable(),
  verificationContactRole: yup.string().nullable(),
  verificationContactPhone: yup.string().nullable(),
})

export type ApplicantJob = yup.InferSubmit<typeof JobSchema>

export const OtherIncomeSchema = yup.object({
  id: yup.string().default(null),
  typeId: yup.mixed<EOtherIncomeType>().required('numberRequired'),
  description: yup
    .string()
    .nullable()
    .max(50)
    .when('typeId', {
      is: EOtherIncomeType.Other,
      then: (schema) => schema.required(),
    }),
  annualAmount: yup
    .integer()
    .required()
    .when('typeId', {
      is: (typeId: number | string) =>
        typeId.toString() === EOtherIncomeType.IncomeAdjustmentToMatchStatements.toString(),
      otherwise: (schema) => schema.min(0),
    }),
})
export type ApplicantOtherIncome = yup.InferSubmit<typeof OtherIncomeSchema>

export const applicantIncomesSchema = yup.object({
  currentJobs: yup
    .array(yup.lazy(() => JobSchema))
    .required()
    .default([]),
  otherIncomes: yup
    .array(yup.lazy(() => OtherIncomeSchema))
    .required()
    .default([]),
  financingProgramId: yup.mixed<EFinancingProgram>().required(),
})

export type ApplicantIncomes = yup.InferSubmit<typeof applicantIncomesSchema>

export const additionnalBankruptciesSchema = yup.object({
  id: yup.string().default(null),
  filedD: yup.date().required(),
  liabilityAmount: yup.integer().required(),
  intentCode: yup.string().required(),
  caseNumber: yup.string().required(),
  trusteeName: yup.string().nullable(),
})
export type AdditionnalBankruptcies = yup.InferSubmit<typeof additionnalBankruptciesSchema>

export const applicantBankruptciesSchema = yup.object({
  additionnalBankruptcies: yup
    .array(yup.lazy(() => additionnalBankruptciesSchema))
    .required()
    .default([]),
})

export type ApplicantBankruptcies = yup.InferSubmit<typeof applicantBankruptciesSchema>

const unregisteredMerchantNameSchemaObject = () => ({
  unregisteredMerchantName: yup.string().max(50).default('').nullable(),
})
export const UnregisteredMerchantNameSchema = yup.object(unregisteredMerchantNameSchemaObject())

export type UnregisteredMerchantName = yup.InferSubmit<typeof UnregisteredMerchantNameSchema>

const draftApplicantSchemaObject = (requireTimeLivedAtCurrentAddress = true) => ({
  id: yup.string().nullable().default(null), // le default null est nécessaire pour le createEntityAdapter
  middleName: yup.string().validCharacters().max(50).default('').nullable(),
  relationWithApplicant: yup
    .mixed<ERelationToApplicant>()
    .nullable()
    .default('' as ERelationToApplicant)
    .when('isPrimaryApplicant', {
      is: false,
      then: (schema) => schema.required().oneOf(getERelationToApplicantList()),
      otherwise: (schema) => schema.notRequired(),
    }),
  firstName: yup.string().validCharacters().max(50).required().default(''),
  lastName: yup.string().validCharacters().max(50).required().default(''),
  birthDate: yup.string().default('').dateOnly().validAgeRequirement(yup.ref('currentAddress.stateIso')),
  sin: yup.string().validSIN().default('').nullable(),
  languageId: yup.integer(),
  genderId: yup.integer().default(null),
  maritalStatusId: yup.integer().nullable(),
  homePhone: yup.string().default('').validCanadianPhone().nullable(),
  cellPhone: yup.string().default('').validCanadianPhone(),
  email: yup.string().required().email().max(60).default(''),
  // Incomes
  currentJobs: yup.array(JobSchema).default([]),
  previousJobs: yup.array(JobSchema).default([]),
  otherIncomes: yup.array(OtherIncomeSchema).default([]),
  // address
  currentAddress: requireTimeLivedAtCurrentAddress ? ApplicantAddressSchema : ApplicantAddressSchemaWithoutTimeLived,
  previousAddresses: yup.array().of(ApplicantAddressSchema),
  additionnalBankruptcies: yup.array(additionnalBankruptciesSchema).default([]),
  expenses: ExpensesSchema.notRequired().default(ExpensesSchema.getDefault()),
  isPrimaryApplicant: yup.bool().required().default(true),
})

export const DraftApplicantSchema = (requireTimeLivedAtCurrentAddress = true) =>
  yup.object(draftApplicantSchemaObject(requireTimeLivedAtCurrentAddress))

export const FullApplicantSchema = () => {
  const schema = draftApplicantSchemaObject()
  return yup.object({
    ...schema,
    birthDate: schema.birthDate.required().validDate(),
    genderId: schema.genderId.required(),
    languageId: schema.languageId.required(),
    maritalStatusId: schema.maritalStatusId.required(),
    cellPhone: schema.cellPhone.validCanadianPhone().required(),
    email: schema.email.required(),
    currentAddress: FullAddressSchema,
  })
}

const releaseRequiredByLoanId = {
  loanId: yup.string().default(''),
  isRequired: yup.boolean(),
}

export const ReleaseRequiredByLoanId = yup.object(releaseRequiredByLoanId)

export interface CreditApplicationSchemaContext {
  financingConfig?: FinancingConfigDto
  merchantServiceCategory?: ELoanPurpose | null
}

export const buildDraftCreditApplicationSchemaObject = (context?: CreditApplicationSchemaContext) => {
  const financingConfig = context?.financingConfig
  const requireTimeLivedAtCurrentAddress = financingConfig?.requiresDurationAtCurrentAddress !== false
  const draftCreditApplicationSchema = {
    id: yup.string().default(''), // le default null est nécessaire pour le createEntityAdapter
    financingProgramId: yup.mixed<EFinancingProgram>().required().oneOf(getEFinancingProgramList()),
    isSinglePayment: yup.bool().default(false),
    isInvisalign: yup.bool().default(false),
    isSymTechPrime: yup.bool().default(false),
    loanPurposeId: yup
      .mixed<ELoanPurpose>()
      .nullable()
      .default('' as ELoanPurpose)
      .when('financingProgramId', {
        is: EFinancingProgram.Personal,
        then: (schema) =>
          schema
            .required('Loan purpose is required for Personal financing')
            .oneOf(getELoanPurposeList(), 'Invalid loan purpose'),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    requestedLoanAmount: yup
      .double()
      .nullable()
      .default(null)
      .when(['financingProgramId', 'loanPurposeId'], ([financingProgramId, loanPurposeId], schema) => {
        const isRequired: boolean = get(
          Constants.financingProgramConfigs,
          (financingProgramId as EFinancingProgram)?.toLowerCase(),
          defaultProgramConfig,
        ).requestedLoanAmountIsRequired

        if (isRequired && financingConfig) {
          const maxLoanAmount = resolveMaxLoanAmount(
            financingProgramId as EFinancingProgram,
            financingConfig.maximumLoanAmount,
            loanPurposeId as ELoanPurpose,
          )
          return schema
            .transform((value: unknown) => (value === '' ? null : value))
            .min(financingConfig.minimumLoanAmount)
            .max(maxLoanAmount)
        }
        return schema.notRequired().nullable()
      }),

    merchantId: yup.string().required().default(''),
    status: yup
      .mixed<ECreditApplicationStatus>()
      .oneOf(getECreditApplicationStatusList())
      .required()
      .default(ECreditApplicationStatus.Draft),
    applicant: DraftApplicantSchema(requireTimeLivedAtCurrentAddress).required(),
    coApplicant: DraftApplicantSchema(requireTimeLivedAtCurrentAddress).nullable().default(null),
    editLocked: yup.bool().default(false),
    correspondanceLanguageId: yup.integer(),
    versionTag: yup.string(),
    beneficiaryTypeId: yup
      .numberEnum<EBeneficiaryType>(getEBeneficiaryTypeList())
      .required()
      .default(EBeneficiaryType.Applicant),
    otherBeneficiaryFirstName: yup
      .string()
      .trim()
      .when(['loanPurposeId', 'beneficiaryTypeId'], {
        is: (loanPurposeId: ELoanPurpose, beneficiaryTypeId: EBeneficiaryType) =>
          loanPurposeId === ELoanPurpose.Veterinary ||
          loanPurposeId === ELoanPurpose.GoodsAndServices ||
          beneficiaryTypeId !== EBeneficiaryType.Other,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(),
      }),

    otherBeneficiaryLastName: yup
      .string()
      .trim()
      .when(['loanPurposeId', 'beneficiaryTypeId'], {
        is: (loanPurposeId: ELoanPurpose, beneficiaryTypeId: EBeneficiaryType) =>
          loanPurposeId === ELoanPurpose.GoodsAndServices || beneficiaryTypeId !== EBeneficiaryType.Other,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(),
      }),
    originSystemId: yup
      .mixed<EOriginSystemId>()
      .oneOf(getEOriginSystemIdList())
      .required()
      .default(EOriginSystemId.Backoffice),
    brokerReferenceNumber: yup
      .integer()
      .nullable()
      .when('financingProgramId', {
        is: (value: EFinancingProgram) =>
          value === EFinancingProgram.AutoRsla || value === EFinancingProgram.CreditRsla,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
    merchantReferenceCode: yup
      .string()
      .nullable()
      .max(20)
      .when('financingProgramId', {
        is: EFinancingProgram.EasyCareProtectionPlan,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
    ...unregisteredMerchantNameSchemaObject(),
  }
  return draftCreditApplicationSchema
}

export const buildDraftCreditApplicationSchema = (context?: CreditApplicationSchemaContext) => {
  return yup.object(buildDraftCreditApplicationSchemaObject(context)).test(
    'test-DistinctCellphone',
    () => t('common.errors.distinctCellphone'),
    function (value) {
      const { createError } = this
      if (value?.coApplicant !== null) {
        const coappCellPhone = value.coApplicant.cellPhone
        const appCellphone = value.applicant.cellPhone
        if (appCellphone && coappCellPhone && value) {
          return (
            appCellphone !== coappCellPhone ||
            createError({ path: 'coApplicant.cellPhone', message: t('common.errors.distinctCellphone') })
          )
        }
      }
      return true
    },
  )
}
export const DraftCreditApplicationSchema = buildDraftCreditApplicationSchema()

export const buildFullCreditApplicationSchema = (context?: CreditApplicationSchemaContext) => {
  const draftSchemaObject = buildDraftCreditApplicationSchemaObject(context)

  const schema = buildDraftCreditApplicationSchema(context).shape({
    merchantId: draftSchemaObject.merchantId.required(),
    requestedLoanAmount: draftSchemaObject.requestedLoanAmount.required(),
    applicant: FullApplicantSchema().required(),
    coApplicant: FullApplicantSchema().nullable(),
  })
  return schema
}
export const FullCreditApplicationSchema = buildFullCreditApplicationSchema()

export type Applicant = yup.InferSubmit<ReturnType<typeof DraftApplicantSchema>> & {
  creditDecision: ApplicantCreditDecision
  softHitReport: SoftHitReport
  hardHitReport: HardHitReport
  prequalificationDecision: PrequalificationDecision
  existingCustomerReference: ExistingCustomerReference | null
}

export interface ProofsOfRelease {
  creditReportUniqueNumber: string
  releaseRequiredByLoanId: Record<string, boolean>
  decidorFullName: string
  decidorId: string
  updatedOn: Date
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  versionTag: string
}

export const BankAccountSchema = yup.object({
  bankName: yup.string().required().max(50),
  institutionNumber: yup
    .string()
    .required()
    .min(3)
    .max(3)
    .matches(/^[0-9]+$/)
    .notOneOf(BannedBankingInformation.BannedInstitutionNumbers, () => t('common.errors.bannedInstitutionNumber')),
  transitNumber: yup
    .string()
    .required()
    .min(5)
    .max(5)
    .matches(/^[0-9]+$/)
    .notOneOf(BannedBankingInformation.BannedTransitNumbers, () => t('common.errors.bannedTransitNumber')),
  accountNumber: yup
    .string()
    .required()
    .min(7)
    .max(12)
    .matches(/^[0-9]+$/),
  transitNumberConfirm: yup
    .string()
    .oneOf([yup.ref('transitNumber')], () => t('common.errors.transitNumberMismatch'))
    .required(),
  accountNumberConfirm: yup
    .string()
    .oneOf([yup.ref('accountNumber')], () => t('common.errors.accountNumberMismatch'))
    .required(),
})

export type BankAccount = yup.InferSubmit<typeof BankAccountSchema>

export interface JobSchemaContext {
  financingProgramId: EFinancingProgram | null
}

export const useJobSchemaResolver = (context: JobSchemaContext): Resolver<ApplicantJob, JobSchemaContext> => {
  return useMemo(() => yupResolver(JobSchema, { context }), [context])
}

type EditCreditApplicationDto = yup.InferSubmit<typeof FullCreditApplicationSchema>
export type CreditApplication = EditCreditApplicationDto & {
  applicant: Applicant
  coApplicant: Applicant | null
  followUpAgent?: {
    id: string
    firstName: string
    lastName: string
    initials: string
  } | null
  currentRevisionId: string
  referenceNumber: number
  parentReferenceNumber: number | null
  parentCreditApplicationId: string | null
  normsCreditDecision: NormCreditDecision | null
  agentCreditDecision: CreditDecision | null
  finalCreditDecision: CreditDecision
  decisionOverrides: CreditDecisionOverrides | null
  refinancing: Refinancing | null
  createdOn: Date
  updatedOn: Date
  expiresOn: Date
  createdByUserFullname: string
  createdByUserId: string
  correspondanceLanguageId: number
  cvtNumber: number | null
  loanId: number | null
  loanPurposeId: string
  proofsOfRelease: ProofsOfRelease
  paymentBankAccount: BankAccount
  prequalificationDecision: PrequalificationDecision
  merchantPaymentPlanId: EPaymentPlan
  beneficiaryTypeId: number
  otherBeneficiaryFirstName: string
  otherBeneficiaryLastName: string
  consentSoftHit: boolean
  consentHardHit: boolean
  initialRequestedLoanAmount: number | null
  brokerReferenceNumber: number | null
  isRush: boolean
  merchantReferenceCode: string | null
  flinksCompleted: boolean
  fcfNote: string
}
